import type { Knex } from 'knex'
import path from 'node:path'
import fs from 'node:fs/promises'
import { readFileSync, mkdirSync, realpathSync } from 'node:fs'
import { getDb } from './connection.js'
import { logger } from '@kernel/logger/index.js'
import { getMigrationsDir, getCoreMigrationsDir, getProjectMigrationsDir, getProjectPath } from '@kernel/config/paths.js'
import { readDatabaseSchema, type DatabaseSchema, isSystemTable } from './schema-reader.js'
import { getOrderedModules } from '@kernel/engine/index.js'
import { getOrderedModulesInternal } from '@kernel/engine/module-queries.js'
import type { RegisteredModule } from '@kernel/engine/module-store.js'
import type { EntityDefinition, EntityIndex, FieldDefinition } from '@gzl10/nexus-sdk'
import {
  getExpectedColumnType,
  getExpectedColumnNames,
  generateColumnDefinition,
} from './migration-helpers.js'
import { moduleStore } from '@kernel/engine/module-store.js'

/**
 * Migration generation scope.
 * - 'core': only entities from core modules
 * - 'project': only entities from standalone (user) modules
 * - 'plugin:{name}': only entities from a specific plugin
 * - 'all': all entities (default, backwards compat)
 */
export type MigrationScope = 'core' | 'project' | 'all' | `plugin:${string}`

/**
 * MySQL utf8mb4 max index key length = 3072 bytes (768 chars × 4 bytes/char).
 * Warn when a unique/composite index exceeds this limit.
 */
const MYSQL_MAX_INDEX_BYTES = 3072
const MYSQL_BYTES_PER_CHAR = 4

function getColumnIndexBytes(field: FieldDefinition | undefined): number {
  if (!field?.db) return 255 * MYSQL_BYTES_PER_CHAR // default varchar(255)
  const size = field.db.size ?? 255
  if (field.db.type === 'text') return 0 // text can't be in index without prefix
  if (field.db.type === 'string') return size * MYSQL_BYTES_PER_CHAR
  if (field.db.type === 'integer') return 4
  if (field.db.type === 'boolean') return 1
  if (field.db.type === 'datetime' || field.db.type === 'date') return 8
  if (field.db.type === 'uuid') return 16
  return size * MYSQL_BYTES_PER_CHAR
}

function warnIfIndexExceedsMySQLLimit(
  table: string,
  columns: string[],
  unique: boolean,
  fields: Record<string, FieldDefinition> | undefined
): void {
  if (!fields) return
  const totalBytes = columns.reduce((sum, col) => sum + getColumnIndexBytes(fields[col]), 0)
  if (totalBytes > MYSQL_MAX_INDEX_BYTES) {
    const indexType = unique ? 'UNIQUE' : 'INDEX'
    logger.warn(
      `${indexType} on ${table}(${columns.join(', ')}) requires ${totalBytes} bytes — ` +
      `exceeds MySQL utf8mb4 limit of ${MYSQL_MAX_INDEX_BYTES} bytes. ` +
      `Reduce column sizes or restructure the index for MySQL compatibility.`
    )
  }
}

/**
 * Schema diff result.
 */
export interface IndexDiff {
  table: string
  newIndexes: Array<{ columns: string[]; unique: boolean }>
  droppedIndexes: Array<{ name: string; columns: string[]; unique: boolean }>
}

export interface FkChange {
  table: string
  newFks: Array<{ column: string; referencedTable: string; referencedColumn: string; onDelete?: string; onUpdate?: string }>
  droppedFks: Array<{ column: string; referencedTable: string; referencedColumn: string; onDelete?: string; onUpdate?: string }>
  modifiedFks: Array<{ column: string; referencedTable: string; referencedColumn: string; onDelete?: string; onUpdate?: string; oldOnDelete?: string; oldOnUpdate?: string }>
}

export interface SchemaDiff {
  newTables: Array<{ table: string; entity: EntityDefinition }>
  droppedTables: string[]
  alteredTables: Array<{
    table: string
    newColumns: Array<{ name: string; type: string; field: FieldDefinition }>
    droppedColumns: Array<{ name: string; type: string }>
    modifiedColumns: Array<{ name: string; oldType: string; newType: string; field: FieldDefinition }>
  }>
  indexChanges: IndexDiff[]
  fkChanges: FkChange[]
}

/**
 * Generate a migration for a specific source scope.
 *
 * @param name - Migration name (without prefix or extension)
 * @param scope - 'core' | 'project' | 'all'
 * @param targetDir - Directory to write the migration file to (auto-detected if not provided)
 * @param prefix - File name prefix (e.g. 'core__') — auto-detected if not provided
 * @returns Filepath of the generated migration, or empty string if no changes
 */
export async function generateMigrationForSource(
  name: string,
  scope: MigrationScope = 'all',
  targetDir?: string,
  prefix?: string,
): Promise<string> {
  const knex = getDb()

  logger.info({ name, scope }, 'Generating migration...')

  // 1. Get entities filtered by scope
  const allEntities = scope === 'all'
    ? getAllPersistentEntities()
    : getPersistentEntitiesByScope(scope)

  if (allEntities.length === 0) {
    logger.warn({ scope }, 'No persistent entities found for scope')
    return ''
  }

  // 2. Read current database schema
  const currentSchema = await readDatabaseSchema(knex)

  // 3. Compare and detect changes
  // Build the set of tables owned by this scope so that drop detection
  // only considers tables that belong to the current scope.
  const ownedTables = collectOwnedTables(allEntities)
  const changes = computeSchemaDiff(allEntities, currentSchema, { ownedTables })

  if (isEmptyDiff(changes)) {
    logger.info('No schema changes detected')
    console.log('\n✅ No schema changes detected\n')
    return ''
  }

  // 4. Generate code for up() and down()
  const upCode = generateUpCode(changes)
  const downCode = generateDownCode(changes)

  // 5. Resolve target dir and prefix
  const effectiveDir = targetDir ?? resolveTargetDir(scope)
  const effectivePrefix = prefix ?? resolvePrefix(scope)

  // 6. Create migration file (.js — no compilation needed, natively importable)
  const timestamp = Date.now()
  const filename = `${effectivePrefix}${timestamp}_${name}.js`
  const filepath = path.join(effectiveDir, filename)

  await fs.mkdir(effectiveDir, { recursive: true })

  // Disable transaction wrapper when FK changes are present.
  // SQLite PRAGMA foreign_keys cannot be toggled inside a transaction.
  const hasFkChanges = changes.fkChanges.length > 0
  const configExport = hasFkChanges ? `\n// Disable transaction: PRAGMA foreign_keys requires autocommit mode in SQLite\nexport const config = { transaction: false }\n` : ''

  const content = `/** @param {import('knex').Knex} knex */
export async function up(knex) {
${upCode}
}

/** @param {import('knex').Knex} knex */
export async function down(knex) {
${downCode}
}
${configExport}`

  await fs.writeFile(filepath, content, 'utf-8')

  logger.info({ filename, scope }, 'Migration created')
  console.log(`\n✅ Migration created: ${filename}\n`)
  console.log(`Generated changes:`)
  logDiffSummary(changes)
  console.log('')

  return filepath
}


/**
 * Resolve target directory for a migration scope.
 */
function resolveTargetDir(scope: MigrationScope): string {
  if (scope === 'core') return getCoreMigrationsDir()
  if (scope === 'project') return getProjectMigrationsDir()
  if (scope === 'all') return getMigrationsDir()

  // plugin:{name}
  const pluginName = scope.slice('plugin:'.length)
  const plugin = moduleStore.plugins.get(pluginName)
  if (!plugin) throw new Error(`Plugin '${pluginName}' not registered`)

  if (!plugin.migrationsDir) {
    // Auto-create migrations dir in the plugin's package source
    const pkgDir = realpathSync(path.join(getProjectPath(), 'node_modules', plugin.name))
    const defaultDir = path.join(pkgDir, 'migrations')
    mkdirSync(defaultDir, { recursive: true })
    plugin.migrationsDir = defaultDir
  }
  return plugin.migrationsDir
}

/**
 * Resolve file name prefix for a migration scope.
 */
function resolvePrefix(scope: MigrationScope): string {
  if (scope === 'core') return 'core__'
  if (scope === 'project' || scope === 'all') return ''

  // plugin:{name}
  const pluginName = scope.slice('plugin:'.length)
  const plugin = moduleStore.plugins.get(pluginName)
  if (!plugin) throw new Error(`Plugin '${pluginName}' not registered — cannot resolve migration prefix`)
  return `${plugin.code}__`
}

/**
 * Detect the default migration scope based on the current project.
 * If cwd is a plugin package (package.json name matches a registered plugin),
 * returns 'plugin:{name}'. Otherwise returns 'project'.
 */
export function detectDefaultScope(): MigrationScope {
  try {
    const pkgPath = path.join(getProjectPath(), 'package.json')
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8'))
    const pkgName = pkg?.name as string | undefined

    // Running from nexus-backend itself → core scope
    if (pkgName === '@gzl10/nexus-backend') {
      return 'core'
    }

    if (pkgName && moduleStore.plugins.has(pkgName)) {
      return `plugin:${pkgName}`
    }
  } catch {
    // No package.json or unreadable — default to project
  }
  return 'project'
}

/**
 * Generate and apply migration automatically (dev workflow).
 * Auto-detects scope: if running inside a plugin package, generates for that plugin.
 * Otherwise generates for project scope.
 * Similar to `prisma migrate dev`.
 */
export async function devMigration(name?: string, scope?: MigrationScope): Promise<void> {
  const migrationName = name
    ? name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    : 'auto'
  const effectiveScope = scope ?? detectDefaultScope()

  if (effectiveScope !== 'project') {
    logger.info({ scope: effectiveScope }, 'Auto-detected plugin scope')
  }

  const { runMigrations, loadAllMigrationFiles } = await import('./migration-runner.js')
  const { getDb } = await import('./connection.js')

  // 1. Apply pending migrations FIRST so the DB schema is up-to-date
  //    before we compare. Without this, a fresh DB would see all existing
  //    tables as "new" and generate duplicate CREATE TABLE migrations.
  const db = getDb()
  const migrationFiles = await loadAllMigrationFiles()
  const executed = await db('_nexus_migrations')
    .where({ status: 'completed' })
    .select('name')
    .then((rows) => new Set(rows.map((r) => r.name)))

  const pendingBefore = migrationFiles.filter((m) => !executed.has(m.name))

  if (pendingBefore.length > 0) {
    console.log(`\nApplying ${pendingBefore.length} pending migration(s)...\n`)
    await runMigrations()
    console.log('✅ Pending migrations applied\n')
  }

  // 2. Now generate migration by comparing entities vs the up-to-date schema
  const filepath = await generateMigrationForSource(migrationName, effectiveScope)

  // 3. If a new migration was generated, apply it immediately
  if (filepath) {
    console.log('Applying new migration...\n')
    try {
      await runMigrations()
    } catch (applyError) {
      // Clean up the generated file so we don't leave an inconsistent state
      // (file on disk but migration not registered as completed)
      console.error('\n❌ Migration apply failed — cleaning up generated file...\n')
      try {
        await fs.unlink(filepath)
        console.error(`Removed: ${path.basename(filepath)}\n`)
      } catch {
        console.error(`Could not remove ${filepath} — please delete it manually.\n`)
      }
      throw applyError
    }
    console.log('\n✅ Migration applied successfully\n')
  } else if (pendingBefore.length === 0) {
    logger.info({ scope: effectiveScope }, 'No changes detected and no pending migrations')
  }
}

/**
 * Detect schema drift between registered entities and current database schema.
 * Returns null if no drift detected, otherwise returns the SchemaDiff.
 *
 * Similar to Prisma's drift detection - used to verify that the database
 * schema matches the expected state from entity definitions.
 *
 * @param knexInstance - Optional Knex instance (defaults to getDb())
 * @returns SchemaDiff if drift detected, null otherwise
 */
export async function detectSchemaDrift(knexInstance?: Knex): Promise<SchemaDiff | null> {
  const knex = knexInstance ?? getDb()

  // Get all persistent entities from registered modules
  const entities = getAllPersistentEntities()

  // If no entities, no drift possible
  if (entities.length === 0) {
    return null
  }

  // Read current database schema
  const currentSchema = await readDatabaseSchema(knex)

  // Compare and detect changes (all entities loaded — detect drops for all)
  const ownedTables = collectOwnedTables(entities)
  const diff = computeSchemaDiff(entities, currentSchema, { ownedTables })

  // Return null if no drift, otherwise return the diff
  return isEmptyDiff(diff) ? null : diff
}

/** Entity types that have their own database table */
const PERSISTENT_TYPES = new Set([
  'collection',
  'tree',
  'dag',
  'event',      // deprecated alias → collection
  'reference',  // deprecated alias → collection
  'temp',       // deprecated alias → collection
  // 'config' excluded: deprecated alias → single (shared single_records table)
])

/**
 * Get all persistent entities from all modules.
 * Only includes entities that have their own database table.
 *
 * Excluded types:
 * - single: stored in shared 'single_records' table
 * - action: no persistence
 * - external/virtual/computed: no database storage
 *
 * @remarks
 * Exported for testing purposes to verify plugin entity detection.
 */
export function getAllPersistentEntities(): EntityDefinition[] {
  const modules = getOrderedModules()
  return extractEntitiesFromModules(modules)
}

/**
 * Get persistent entities filtered by module source scope.
 * - 'core': entities from modules with _source === 'core'
 * - 'project': entities from modules with _source === 'standalone'
 * - 'plugin:{name}': entities from a specific plugin
 */
export function getPersistentEntitiesByScope(scope: 'core' | 'project' | `plugin:${string}`): EntityDefinition[] {
  const modules = getOrderedModulesInternal()

  let filtered: RegisteredModule[]

  if (scope.startsWith('plugin:')) {
    const pluginName = scope.slice('plugin:'.length)
    filtered = modules.filter((m) => m._source === 'plugin' && m._pluginName === pluginName)
  } else {
    const sourceFilter = scope === 'core' ? 'core' : 'standalone'
    filtered = modules.filter((m) => m._source === sourceFilter)
  }

  return extractEntitiesFromModules(filtered)
}

/**
 * Extract persistent entities from a list of modules.
 */
function extractEntitiesFromModules(modules: readonly { definitions?: EntityDefinition[] }[]): EntityDefinition[] {
  const entities: EntityDefinition[] = []

  for (const mod of modules) {
    if (!mod.definitions) continue

    for (const def of mod.definitions) {
      const entityType = def.type ?? 'collection'
      if (!PERSISTENT_TYPES.has(entityType)) continue

      // Skip entities with external adapter (e.g., in-memory masters)
      if ((def as any).adapter) continue

      if (!(def as any).table) {
        logger.warn({ entity: def, type: def.type }, 'Persistent entity missing table property')
        continue
      }

      entities.push(def)
    }
  }

  return entities
}

/**
 * Collect the set of table names owned by the given entities.
 * Includes DAG junction tables which are auto-generated.
 */
export function collectOwnedTables(entities: EntityDefinition[]): Set<string> {
  const tables = new Set<string>()
  for (const entity of entities) {
    const tableName = (entity as any).table as string
    if (tableName) tables.add(tableName)
    // DAG entities auto-create a junction table
    if (entity.type === 'dag') {
      const parentsTable = (entity as EntityDefinition & { parentsTable?: string }).parentsTable ?? `${tableName}_parents`
      tables.add(parentsTable)
    }
  }
  return tables
}

/**
 * Compare entities vs current schema and detect changes.
 *
 * @remarks Exported for testing purposes.
 */
export function computeSchemaDiff(
  entities: EntityDefinition[],
  currentSchema: DatabaseSchema,
  options?: { ownedTables?: Set<string> }
): SchemaDiff {
  const changes: SchemaDiff = {
    newTables: [],
    droppedTables: [],
    alteredTables: [],
    indexChanges: [],
    fkChanges: [],
  }

  // 1. Detect new tables and altered tables
  for (const entity of entities) {
    const tableName = (entity as any).table as string
    const currentTable = currentSchema.tables.get(tableName)

    if (!currentTable) {
      // New table (skip system tables - managed by ensureSystemTables)
      if (!isSystemTable(tableName)) {
        changes.newTables.push({ table: tableName, entity })
      }
    } else {
      // Table exists - compare columns
      const alteration = compareTable(entity, currentTable)
      if (
        alteration.newColumns.length > 0 ||
        alteration.droppedColumns.length > 0 ||
        alteration.modifiedColumns.length > 0
      ) {
        changes.alteredTables.push(alteration)
      }
    }
  }

  // 2. Detect dropped tables — only within ownedTables scope.
  // ownedTables contains the set of tables that belong to the current scope
  // (core, project, or a specific plugin). Only tables in this set that no
  // longer have a matching entity are considered "dropped".
  if (options?.ownedTables && options.ownedTables.size > 0) {
    const entityTables = new Set(entities.map((e) => (e as any).table as string))

    // Include DAG junction tables (auto-generated, not in entity definitions)
    for (const entity of entities) {
      if (entity.type === 'dag') {
        const parentsTable = (entity as EntityDefinition & { parentsTable?: string }).parentsTable ?? `${(entity as any).table}_parents`
        entityTables.add(parentsTable)
      }
    }

    for (const tableName of options.ownedTables) {
      // Table is owned by this scope, exists in DB, but has no entity → dropped
      if (!entityTables.has(tableName) && currentSchema.tables.has(tableName) && !isSystemTable(tableName)) {
        changes.droppedTables.push(tableName)
      }
    }
  }

  // 3. Detect index changes on existing tables
  for (const entity of entities) {
    const tableName = (entity as any).table as string
    const currentTable = currentSchema.tables.get(tableName)
    if (!currentTable) continue // new table — indexes handled in table creation

    const entityIndexes: EntityIndex[] = (entity as EntityDefinition & { indexes?: EntityIndex[] }).indexes || []
    const dbIndexes = currentTable.indexes || []

    // Collect single-column unique/index constraints from field definitions
    // These generate indexes in the DB but aren't in entity.indexes[]
    const fieldIndexKeys = new Set<string>()
    if (entity.fields) {
      for (const [fieldName, field] of Object.entries(entity.fields)) {
        const f = field as FieldDefinition
        if (f.db?.unique) {
          fieldIndexKeys.add(`U:${fieldName}`)
        }
        if (f.db?.index) {
          fieldIndexKeys.add(`I:${fieldName}`)
        }
      }
    }

    // Normalize: sort columns for comparison
    const normalizeKey = (cols: string[], unique: boolean) =>
      `${unique ? 'U' : 'I'}:${[...cols].sort().join(',')}`

    const expectedKeys = new Set([
      ...entityIndexes.map(idx => normalizeKey(idx.columns, !!idx.unique)),
      ...fieldIndexKeys,
    ])
    const currentKeys = new Map(dbIndexes.map(idx => [normalizeKey(idx.columns, idx.unique), idx]))

    const diff: IndexDiff = { table: tableName, newIndexes: [], droppedIndexes: [] }

    // New indexes (in entity but not in DB)
    for (const idx of entityIndexes) {
      const key = normalizeKey(idx.columns, !!idx.unique)
      if (!currentKeys.has(key)) {
        warnIfIndexExceedsMySQLLimit(tableName, idx.columns, !!idx.unique, entity.fields as Record<string, FieldDefinition> | undefined)
        diff.newIndexes.push({ columns: idx.columns, unique: !!idx.unique })
      }
    }

    // Dropped indexes (in DB but not in entity) — only drop indexes that look
    // like they were generated by Knex (table_col1_col2_unique / table_col1_col2_index)
    // to avoid dropping manually created indexes or framework indexes.
    for (const [key, idx] of currentKeys) {
      if (!expectedKeys.has(key)) {
        const looksManaged = idx.name.startsWith(tableName) || idx.name.startsWith('sqlite_autoindex')
        if (looksManaged) {
          diff.droppedIndexes.push({ name: idx.name, columns: idx.columns, unique: idx.unique })
        }
      }
    }

    if (diff.newIndexes.length > 0 || diff.droppedIndexes.length > 0) {
      changes.indexChanges.push(diff)
    }
  }

  // 4. Detect FK changes on existing tables
  for (const entity of entities) {
    const tableName = (entity as any).table as string
    const currentTable = currentSchema.tables.get(tableName)
    if (!currentTable || !entity.fields) continue // new table — FKs handled in table creation

    const fkDiff: FkChange = { table: tableName, newFks: [], droppedFks: [], modifiedFks: [] }

    // Build expected FKs from entity field relations
    const expectedFks = new Map<string, { table: string; column: string; onDelete?: string; onUpdate?: string }>()
    for (const [fieldName, field] of Object.entries(entity.fields) as [string, FieldDefinition][]) {
      if (field.relation?.table) {
        expectedFks.set(fieldName, {
          table: field.relation.table,
          column: field.relation.column || 'id',
          onDelete: field.relation.onDelete,
          onUpdate: field.relation.onUpdate,
        })
      }
    }

    // Also add auto-field FKs (tree parent_id)
    if (entity.type === 'tree') {
      expectedFks.set('parent_id', {
        table: tableName,
        column: 'id',
        onDelete: 'SET NULL',
      })
    }

    // Build current FKs map by column
    const currentFks = new Map<string, { referencedTable: string; referencedColumn: string; onDelete?: string; onUpdate?: string }>()
    for (const fk of currentTable.foreignKeys) {
      currentFks.set(fk.column, {
        referencedTable: fk.referencedTable,
        referencedColumn: fk.referencedColumn,
        onDelete: fk.onDelete,
        onUpdate: fk.onUpdate,
      })
    }

    // Detect new and modified FKs
    for (const [col, expected] of expectedFks) {
      const current = currentFks.get(col)
      if (!current) {
        // Only add FK if column already exists in DB (new columns get FK via generateColumnDefinition)
        if (currentTable.columns.has(col)) {
          fkDiff.newFks.push({
            column: col,
            referencedTable: expected.table,
            referencedColumn: expected.column,
            onDelete: expected.onDelete,
            onUpdate: expected.onUpdate,
          })
        }
      } else {
        // FK exists — check if target or behavior changed
        // Normalize: uppercase, map empty/NO ACTION → undefined for comparison
        const onDeleteChanged = normalizeFkRule(expected.onDelete) !== normalizeFkRule(current.onDelete)
        const onUpdateChanged = normalizeFkRule(expected.onUpdate) !== normalizeFkRule(current.onUpdate)
        const targetChanged = current.referencedTable !== expected.table || current.referencedColumn !== expected.column

        if (targetChanged || onDeleteChanged || onUpdateChanged) {
          fkDiff.modifiedFks.push({
            column: col,
            referencedTable: expected.table,
            referencedColumn: expected.column,
            onDelete: expected.onDelete,
            onUpdate: expected.onUpdate,
            oldOnDelete: current.onDelete,
            oldOnUpdate: current.onUpdate,
          })
        }
      }
    }

    // Detect dropped FKs (in DB but not in entity)
    for (const [col, current] of currentFks) {
      if (!expectedFks.has(col)) {
        fkDiff.droppedFks.push({
          column: col,
          referencedTable: current.referencedTable,
          referencedColumn: current.referencedColumn,
          onDelete: current.onDelete,
          onUpdate: current.onUpdate,
        })
      }
    }

    if (fkDiff.newFks.length > 0 || fkDiff.droppedFks.length > 0 || fkDiff.modifiedFks.length > 0) {
      changes.fkChanges.push(fkDiff)
    }
  }

  return changes
}

/**
 * Compare entity definition vs current table and detect column changes.
 */
function compareTable(
  entity: EntityDefinition,
  currentTable: { name: string; columns: Map<string, { name: string; type: string; nullable: boolean }> }
): {
  table: string
  newColumns: Array<{ name: string; type: string; field: FieldDefinition }>
  droppedColumns: Array<{ name: string; type: string }>
  modifiedColumns: Array<{ name: string; oldType: string; newType: string; field: FieldDefinition }>
} {
  const result = {
    table: (entity as any).table || '',
    newColumns: [] as Array<{ name: string; type: string; field: FieldDefinition }>,
    droppedColumns: [] as Array<{ name: string; type: string }>,
    modifiedColumns: [] as Array<{ name: string; oldType: string; newType: string; field: FieldDefinition }>,
  }

  // Skip if entity has no fields
  if (!entity.fields) {
    return result
  }

  const expectedColumns = getExpectedColumnNames(entity as { fields: Record<string, FieldDefinition> })
  const currentColumnNames = new Set(currentTable.columns.keys())

  // Detect new columns
  for (const [fieldName, field] of Object.entries(entity.fields)) {
    if (!field.db || field.db.virtual) continue

    const columnName = fieldName

    if (!currentColumnNames.has(columnName)) {
      result.newColumns.push({
        name: columnName,
        type: getExpectedColumnType(field),
        field,
      })
    } else {
      // Column exists - check if type changed
      const currentCol = currentTable.columns.get(columnName)!
      const expectedType = getExpectedColumnType(field)

      // Normalize types for comparison
      const currentType = normalizeColumnType(currentCol.type)
      const normalizedExpectedType = normalizeColumnType(expectedType)

      if (currentType !== normalizedExpectedType) {
        result.modifiedColumns.push({
          name: columnName,
          oldType: currentCol.type,
          newType: expectedType,
          field,
        })
      }
    }
  }

  // Check auto-fields that aren't in entity.fields but expected in DB
  const hasSoftDelete = (entity as EntityDefinition & { softDelete?: boolean }).softDelete
  if (hasSoftDelete && !currentColumnNames.has('deleted_at')) {
    result.newColumns.push({
      name: 'deleted_at',
      type: 'TIMESTAMP',
      field: { label: { en: 'Deleted At', es: 'Eliminado' }, input: 'datetime', db: { type: 'datetime', nullable: true } } as FieldDefinition,
    })
  }

  if (entity.type === 'tree' && !currentColumnNames.has('parent_id')) {
    result.newColumns.push({
      name: 'parent_id',
      type: 'STRING',
      field: { label: { en: 'Parent', es: 'Padre' }, input: 'select', db: { type: 'string', size: 26, nullable: true } } as FieldDefinition,
    })
  }

  // Add auto-fields to expected columns for drop detection
  if ((entity as EntityDefinition & { timestamps?: boolean }).timestamps) {
    expectedColumns.add('created_at')
    expectedColumns.add('updated_at')
  }
  if ((entity as EntityDefinition & { audit?: boolean }).audit) {
    expectedColumns.add('created_by')
    expectedColumns.add('updated_by')
  }
  if (hasSoftDelete) expectedColumns.add('deleted_at')
  if (entity.type === 'tree') expectedColumns.add('parent_id')

  // Detect dropped columns (exclude system fields)
  const systemFields = new Set(['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'deleted_at', 'parent_id'])

  for (const columnName of currentColumnNames) {
    if (!expectedColumns.has(columnName) && !systemFields.has(columnName)) {
      const col = currentTable.columns.get(columnName)!
      result.droppedColumns.push({ name: columnName, type: col.type })
    }
  }

  return result
}

/**
 * Normalize column type for comparison.
 *
 * @remarks Exported for testing purposes.
 */
export function normalizeColumnType(type: string): string {
  // Strip size/precision params: VARCHAR(26) → VARCHAR, DECIMAL(10,2) → DECIMAL
  const normalized = type.replace(/\(.*\)/, '').trim().toUpperCase()

  // Map common variants
  const typeMap: Record<string, string> = {
    'VARCHAR': 'STRING',
    'CHAR': 'STRING',
    'TEXT': 'TEXT',
    'INT': 'INTEGER',
    'INTEGER': 'INTEGER',
    'BIGINT': 'BIGINTEGER',
    'FLOAT': 'DECIMAL',
    'DOUBLE': 'DECIMAL',
    'DECIMAL': 'DECIMAL',
    'NUMERIC': 'DECIMAL',
    'BOOLEAN': 'BOOLEAN',
    'BOOL': 'BOOLEAN',
    'TIMESTAMP': 'DATETIME',
    'DATETIME': 'DATETIME',
    'DATE': 'DATE',
    'TIME': 'TIME',
    'JSON': 'JSON',
    'JSONB': 'JSON',
    'UUID': 'UUID',
    // PostgreSQL specific (information_schema.columns.data_type values)
    'CHARACTER VARYING': 'STRING',
    'CHARACTER': 'STRING',
    'DOUBLE PRECISION': 'DECIMAL',
    'REAL': 'DECIMAL',
    'SMALLINT': 'INTEGER',
    'TIMESTAMP WITHOUT TIME ZONE': 'DATETIME',
    'TIMESTAMP WITH TIME ZONE': 'DATETIME',
    'TIME WITHOUT TIME ZONE': 'TIME',
    'TIME WITH TIME ZONE': 'TIME',
    'BIT VARYING': 'STRING',
    // MySQL specific
    'TINYINT': 'BOOLEAN',
    'LONGTEXT': 'TEXT',
    'MEDIUMTEXT': 'TEXT',
  }

  return typeMap[normalized] || normalized
}

/**
 * Normalize FK rule (onDelete/onUpdate) for comparison.
 * Maps empty, 'NO ACTION', and 'RESTRICT' → undefined.
 * Uppercases for case-insensitive comparison.
 *
 * @remarks Exported for testing purposes.
 */
export function normalizeFkRule(rule: string | undefined): string | undefined {
  if (!rule) return undefined
  const upper = rule.toUpperCase().trim()
  if (upper === 'NO ACTION' || upper === 'RESTRICT' || upper === '') return undefined
  return upper
}

/**
 * Map a raw DB column type to a Knex builder call for down() migration generation.
 * Uses the original DB type to produce a best-effort recreation statement.
 */
function mapDbTypeToKnex(dbType: string, colName?: string): { code: string } {
  const name = colName ? `'${colName}'` : `'column'`
  const upper = dbType.replace(/\(.*\)/, '').trim().toUpperCase()

  // Extract size if present: VARCHAR(26) → 26
  const sizeMatch = dbType.match(/\((\d+)\)/)
  const size = sizeMatch ? sizeMatch[1] : null

  const mapping: Record<string, string> = {
    'VARCHAR': size ? `table.string(${name}, ${size}).nullable()` : `table.string(${name}).nullable()`,
    'CHARACTER VARYING': size ? `table.string(${name}, ${size}).nullable()` : `table.string(${name}).nullable()`,
    'CHAR': size ? `table.string(${name}, ${size}).nullable()` : `table.string(${name}).nullable()`,
    'TEXT': `table.text(${name}).nullable()`,
    'INT': `table.integer(${name}).nullable()`,
    'INTEGER': `table.integer(${name}).nullable()`,
    'BIGINT': `table.bigInteger(${name}).nullable()`,
    'FLOAT': `table.float(${name}).nullable()`,
    'DOUBLE': `table.float(${name}).nullable()`,
    'DOUBLE PRECISION': `table.float(${name}).nullable()`,
    'DECIMAL': `table.decimal(${name}).nullable()`,
    'NUMERIC': `table.decimal(${name}).nullable()`,
    'BOOLEAN': `table.boolean(${name}).nullable()`,
    'BOOL': `table.boolean(${name}).nullable()`,
    'TINYINT': `table.boolean(${name}).nullable()`,
    'TIMESTAMP': `table.timestamp(${name}).nullable()`,
    'DATETIME': `table.timestamp(${name}).nullable()`,
    'TIMESTAMP WITHOUT TIME ZONE': `table.timestamp(${name}).nullable()`,
    'TIMESTAMP WITH TIME ZONE': `table.timestamp(${name}).nullable()`,
    'DATE': `table.date(${name}).nullable()`,
    'TIME': `table.time(${name}).nullable()`,
    'JSON': `table.json(${name}).nullable()`,
    'JSONB': `table.jsonb(${name}).nullable()`,
    'UUID': `table.uuid(${name}).nullable()`,
  }

  return { code: mapping[upper] || `table.specificType(${name}, '${dbType}').nullable()` }
}

/**
 * Check if diff is empty.
 */
function isEmptyDiff(diff: SchemaDiff): boolean {
  return (
    diff.newTables.length === 0 &&
    diff.droppedTables.length === 0 &&
    diff.alteredTables.length === 0 &&
    diff.indexChanges.length === 0 &&
    diff.fkChanges.length === 0
  )
}

/**
 * Sort tables topologically based on FK dependencies.
 * Tables that are referenced by FKs come first.
 *
 * @remarks Exported for testing purposes.
 */
export function topologicalSortTables(
  tables: Array<{ table: string; entity: EntityDefinition }>
): Array<{ table: string; entity: EntityDefinition }> {
  if (tables.length <= 1) return tables

  // Build dependency graph: table -> tables it depends on
  const dependencies = new Map<string, Set<string>>()
  const tableMap = new Map<string, { table: string; entity: EntityDefinition }>()

  for (const item of tables) {
    tableMap.set(item.table, item)
    dependencies.set(item.table, new Set())

    // Find FK dependencies from field relations
    if (item.entity.fields) {
      for (const field of Object.values(item.entity.fields) as FieldDefinition[]) {
        if (field.relation?.table && field.relation.table !== item.table) {
          dependencies.get(item.table)!.add(field.relation.table)
        }
      }
    }
  }

  // Kahn's algorithm for topological sort
  const sorted: Array<{ table: string; entity: EntityDefinition }> = []
  const inDegree = new Map<string, number>()

  // Calculate in-degree (only for tables in our list)
  for (const [table] of dependencies) {
    inDegree.set(table, 0)
  }
  for (const [table, deps] of dependencies) {
    for (const dep of deps) {
      if (inDegree.has(dep)) {
        inDegree.set(table, (inDegree.get(table) || 0) + 1)
      }
    }
  }

  // Start with tables that have no dependencies
  const queue: string[] = []
  for (const [table, degree] of inDegree) {
    if (degree === 0) queue.push(table)
  }

  while (queue.length > 0) {
    const table = queue.shift()!
    const item = tableMap.get(table)
    if (item) sorted.push(item)

    // Reduce in-degree of dependent tables
    for (const [otherTable, deps] of dependencies) {
      if (deps.has(table) && inDegree.has(otherTable)) {
        const newDegree = (inDegree.get(otherTable) || 0) - 1
        inDegree.set(otherTable, newDegree)
        if (newDegree === 0) queue.push(otherTable)
      }
    }
  }

  // If not all tables were sorted, there's a cycle - return original order
  if (sorted.length !== tables.length) {
    logger.warn('Circular FK dependency detected, using original order')
    return tables
  }

  return sorted
}

/**
 * Generate Knex code for up().
 *
 * @remarks Exported for testing purposes.
 */
export function generateUpCode(changes: SchemaDiff): string {
  const lines: string[] = []

  // Sort tables topologically to handle FK dependencies
  const sortedNewTables = topologicalSortTables(changes.newTables)

  // 1. Create new tables (with hasTable check for safety)
  for (const { table, entity } of sortedNewTables) {
    lines.push(`  if (!(await knex.schema.hasTable('${table}'))) {`)
    lines.push(`    await knex.schema.createTable('${table}', (table) => {`)

    // Generate columns from fields
    const fieldNames = new Set(Object.keys(entity.fields ?? {}))
    if (entity.fields) {
      for (const [fieldName, field] of Object.entries(entity.fields) as [string, FieldDefinition][]) {
        if (!field.db || field.db.virtual) continue

        let columnDef = generateColumnDefinition(fieldName, field)
        // Auto-add primary key to 'id' field if not already defined
        if (fieldName === 'id' && !field.db.primary && !columnDef.includes('.primary()')) {
          columnDef += '.primary()'
        }
        // Warn if single-column unique/index exceeds MySQL limit
        if (field.db.unique || field.db.index) {
          warnIfIndexExceedsMySQLLimit(table, [fieldName], !!field.db.unique, entity.fields)
        }
        lines.push(`      ${columnDef}`)
      }
    }

    // Add timestamps if entity has timestamps: true (skip if already declared in fields)
    const hasTimestamps = (entity as EntityDefinition & { timestamps?: boolean }).timestamps
    if (hasTimestamps) {
      if (!fieldNames.has('created_at') || !fieldNames.has('updated_at')) lines.push(`      // timestamps`)
      if (!fieldNames.has('created_at')) lines.push(`      table.timestamp('created_at').nullable().defaultTo(knex.fn.now())`)
      if (!fieldNames.has('updated_at')) lines.push(`      table.timestamp('updated_at').nullable().defaultTo(knex.fn.now())`)
    }

    // Add audit fields if entity has audit: true (skip if already declared in fields)
    const hasAudit = (entity as EntityDefinition & { audit?: boolean }).audit
    if (hasAudit) {
      if (!fieldNames.has('created_by') || !fieldNames.has('updated_by')) lines.push(`      // audit`)
      if (!fieldNames.has('created_by')) lines.push(`      table.string('created_by', 26).nullable()`)
      if (!fieldNames.has('updated_by')) lines.push(`      table.string('updated_by', 26).nullable()`)
    }

    // Add soft delete column if entity has softDelete: true (skip if already declared in fields)
    const hasSoftDelete = (entity as EntityDefinition & { softDelete?: boolean }).softDelete
    if (hasSoftDelete) {
      if (!fieldNames.has('deleted_at')) lines.push(`      // soft delete`)
      if (!fieldNames.has('deleted_at')) lines.push(`      table.timestamp('deleted_at').nullable()`)
    }

    // Add parent_id for tree entities (auto-field injected by TreeService at runtime)
    if (entity.type === 'tree') {
      lines.push(`      // tree parent reference`)
      lines.push(`      table.string('parent_id', 26).nullable().references('${table}.id').onDelete('SET NULL')`)
    }

    // Create indexes if defined (skip single-column unique indexes already declared on column)
    const entityWithIndexes = entity as EntityDefinition & { indexes?: EntityIndex[] }
    if (entityWithIndexes.indexes?.length) {
      const fieldsWithUnique = new Set<string>()
      if (entity.fields) {
        for (const [name, f] of Object.entries(entity.fields) as [string, FieldDefinition][]) {
          if (f.db?.unique) fieldsWithUnique.add(name)
        }
      }

      for (const idx of entityWithIndexes.indexes) {
        if (idx.unique && idx.columns.length === 1 && fieldsWithUnique.has(idx.columns[0]!)) {
          continue
        }
        warnIfIndexExceedsMySQLLimit(table, idx.columns, !!idx.unique, entity.fields)
        const cols = idx.columns.map(c => `'${c}'`).join(', ')
        if (idx.unique) {
          lines.push(`      table.unique([${cols}])`)
        } else {
          lines.push(`      table.index([${cols}])`)
        }
      }
    }

    lines.push(`    })`)
    lines.push(`  }`)
    lines.push('')

    // Create DAG junction table for parent relationships
    if (entity.type === 'dag') {
      const parentsTable = (entity as EntityDefinition & { parentsTable?: string }).parentsTable ?? `${table}_parents`
      lines.push(`  if (!(await knex.schema.hasTable('${parentsTable}'))) {`)
      lines.push(`    await knex.schema.createTable('${parentsTable}', (table) => {`)
      lines.push(`      table.string('node_id', 26).notNullable().references('${(entity as any).table}.id').onDelete('CASCADE')`)
      lines.push(`      table.string('parent_id', 26).notNullable().references('${(entity as any).table}.id').onDelete('CASCADE')`)
      lines.push(`      table.primary(['node_id', 'parent_id'])`)
      lines.push(`    })`)
      lines.push(`  }`)
      lines.push('')
    }
  }

  // 2. Alter existing tables
  for (const alteration of changes.alteredTables) {
    const hasChanges =
      alteration.newColumns.length > 0 ||
      alteration.droppedColumns.length > 0 ||
      alteration.modifiedColumns.length > 0

    if (!hasChanges) continue

    lines.push(`  await knex.schema.alterTable('${alteration.table}', (table) => {`)

    // Add new columns
    for (const col of alteration.newColumns) {
      const columnDef = generateColumnDefinition(col.name, col.field)
      lines.push(`    ${columnDef}`)
    }

    // Modify columns: drop in this alterTable (recreate in separate one below)
    for (const col of alteration.modifiedColumns) {
      lines.push(`    // Modify ${col.name}: ${col.oldType} → ${col.newType} (⚠️ DATA LOSS)`)
      lines.push(`    table.dropColumn('${col.name}')`)
    }

    // Drop columns
    for (const col of alteration.droppedColumns) {
      lines.push(`    table.dropColumn('${col.name}')`)
    }

    lines.push(`  })`)
    lines.push('')

    // Recreate modified columns in a separate alterTable (required for SQLite)
    if (alteration.modifiedColumns.length > 0) {
      lines.push(`  await knex.schema.alterTable('${alteration.table}', (table) => {`)
      for (const col of alteration.modifiedColumns) {
        const columnDef = generateColumnDefinition(col.name, col.field)
        lines.push(`    ${columnDef}`)
      }
      lines.push(`  })`)
      lines.push('')
    }
  }

  // 3. Index changes on existing tables
  for (const indexDiff of changes.indexChanges) {
    if (indexDiff.newIndexes.length === 0 && indexDiff.droppedIndexes.length === 0) continue

    lines.push(`  await knex.schema.alterTable('${indexDiff.table}', (table) => {`)

    for (const idx of indexDiff.droppedIndexes) {
      const cols = idx.columns.map(c => `'${c}'`).join(', ')
      if (idx.unique) {
        lines.push(`    table.dropUnique([${cols}])`)
      } else {
        lines.push(`    table.dropIndex([${cols}])`)
      }
    }

    for (const idx of indexDiff.newIndexes) {
      const cols = idx.columns.map(c => `'${c}'`).join(', ')
      if (idx.unique) {
        lines.push(`    table.unique([${cols}])`)
      } else {
        lines.push(`    table.index([${cols}])`)
      }
    }

    lines.push(`  })`)
    lines.push('')
  }

  // 4. FK changes on existing tables
  // SQLite requires PRAGMA foreign_keys = OFF to alter tables with FK constraints
  // (Knex recreates the table via temp table, which fails with FK checks enabled)
  if (changes.fkChanges.length > 0) {
    lines.push(`  // Disable FK checks for SQLite compatibility (Knex rebuilds tables via temp copy)`)
    lines.push(`  const isSQLite = ['better-sqlite3', 'sqlite3', 'sqlite'].includes(knex.client.config.client)`)
    lines.push(`  if (isSQLite) await knex.raw('PRAGMA foreign_keys = OFF')`)
    lines.push(`  try {`)
    lines.push('')
  }

  for (const fkDiff of changes.fkChanges) {
    const hasChanges = fkDiff.newFks.length > 0 || fkDiff.droppedFks.length > 0 || fkDiff.modifiedFks.length > 0
    if (!hasChanges) continue

    // Drop removed FKs and old version of modified FKs
    const fksToDrop = [...fkDiff.droppedFks, ...fkDiff.modifiedFks]
    if (fksToDrop.length > 0) {
      lines.push(`  await knex.schema.alterTable('${fkDiff.table}', (table) => {`)
      for (const fk of fksToDrop) {
        lines.push(`    table.dropForeign(['${fk.column}'])`)
      }
      lines.push(`  })`)
      lines.push('')
    }

    // Add new FKs and new version of modified FKs
    const fksToAdd = [...fkDiff.newFks, ...fkDiff.modifiedFks]
    if (fksToAdd.length > 0) {
      lines.push(`  await knex.schema.alterTable('${fkDiff.table}', (table) => {`)
      for (const fk of fksToAdd) {
        let ref = `    table.foreign('${fk.column}').references('${fk.referencedColumn}').inTable('${fk.referencedTable}')`
        if (fk.onDelete) ref += `.onDelete('${fk.onDelete}')`
        if (fk.onUpdate) ref += `.onUpdate('${fk.onUpdate}')`
        lines.push(ref)
      }
      lines.push(`  })`)
      lines.push('')
    }
  }

  if (changes.fkChanges.length > 0) {
    lines.push(`  } finally {`)
    lines.push(`    if (isSQLite) await knex.raw('PRAGMA foreign_keys = ON')`)
    lines.push(`  }`)
    lines.push('')
  }

  // 5. Drop tables (usually dangerous, but included for completeness)
  for (const tableName of changes.droppedTables) {
    lines.push(`  await knex.schema.dropTableIfExists('${tableName}')`)
  }

  if (lines.length === 0) {
    lines.push('  // No changes')
  }

  return lines.join('\n')
}

/**
 * Generate Knex code for down() (inverse of up).
 *
 * @remarks Exported for testing purposes.
 */
export function generateDownCode(changes: SchemaDiff): string {
  const lines: string[] = []

  // Revert in reverse order

  // 1. Recreate dropped tables (reverse of drop)
  for (const tableName of changes.droppedTables.slice().reverse()) {
    lines.push(`  // NOTE: Cannot recreate dropped table '${tableName}' without schema definition`)
    lines.push(`  // await knex.schema.createTable('${tableName}', (table) => { /* ... */ })`)
  }

  // 2. Revert table alterations (reverse order)
  for (const alteration of changes.alteredTables.slice().reverse()) {
    const hasChanges =
      alteration.newColumns.length > 0 ||
      alteration.droppedColumns.length > 0 ||
      alteration.modifiedColumns.length > 0

    if (!hasChanges) continue

    lines.push(`  await knex.schema.alterTable('${alteration.table}', (table) => {`)

    // Recreate dropped columns (using DB type from before the drop)
    for (const col of alteration.droppedColumns) {
      const knexType = mapDbTypeToKnex(col.type, col.name)
      lines.push(`    ${knexType.code} // was: ${col.type}`)
    }

    // Revert modified columns: drop in this alterTable (recreate in separate one below)
    for (const col of alteration.modifiedColumns.slice().reverse()) {
      lines.push(`    // Revert ${col.name}: ${col.newType} → ${col.oldType} (⚠️ DATA LOSS)`)
      lines.push(`    table.dropColumn('${col.name}')`)
    }

    // Drop new columns
    for (const col of alteration.newColumns.slice().reverse()) {
      lines.push(`    table.dropColumn('${col.name}')`)
    }

    lines.push(`  })`)
    lines.push('')

    // Recreate modified columns with original types
    if (alteration.modifiedColumns.length > 0) {
      lines.push(`  await knex.schema.alterTable('${alteration.table}', (table) => {`)
      for (const col of alteration.modifiedColumns.slice().reverse()) {
        const knexType = mapDbTypeToKnex(col.oldType, col.name)
        lines.push(`    ${knexType.code} // was: ${col.oldType}`)
      }
      lines.push(`  })`)
      lines.push('')
    }
  }

  // 3. Revert index changes (inverse: new→drop, dropped→create)
  for (const indexDiff of changes.indexChanges.slice().reverse()) {
    if (indexDiff.newIndexes.length === 0 && indexDiff.droppedIndexes.length === 0) continue

    lines.push(`  await knex.schema.alterTable('${indexDiff.table}', (table) => {`)

    // Drop indexes that were added in up()
    for (const idx of indexDiff.newIndexes) {
      const cols = idx.columns.map(c => `'${c}'`).join(', ')
      if (idx.unique) {
        lines.push(`    table.dropUnique([${cols}])`)
      } else {
        lines.push(`    table.dropIndex([${cols}])`)
      }
    }

    // Recreate indexes that were dropped in up()
    for (const idx of indexDiff.droppedIndexes) {
      const cols = idx.columns.map(c => `'${c}'`).join(', ')
      if (idx.unique) {
        lines.push(`    table.unique([${cols}])`)
      } else {
        lines.push(`    table.index([${cols}])`)
      }
    }

    lines.push(`  })`)
    lines.push('')
  }

  // 4. Revert FK changes (inverse: new→drop, dropped→create, modified→swap back)
  if (changes.fkChanges.length > 0) {
    lines.push(`  // Disable FK checks for SQLite compatibility`)
    lines.push(`  const isSQLite = ['better-sqlite3', 'sqlite3', 'sqlite'].includes(knex.client.config.client)`)
    lines.push(`  if (isSQLite) await knex.raw('PRAGMA foreign_keys = OFF')`)
    lines.push(`  try {`)
    lines.push('')
  }

  for (const fkDiff of changes.fkChanges.slice().reverse()) {
    const hasChanges = fkDiff.newFks.length > 0 || fkDiff.droppedFks.length > 0 || fkDiff.modifiedFks.length > 0
    if (!hasChanges) continue

    // Drop FKs that were added in up() + drop new version of modified FKs
    const fksToDrop = [...fkDiff.newFks, ...fkDiff.modifiedFks]
    if (fksToDrop.length > 0) {
      lines.push(`  await knex.schema.alterTable('${fkDiff.table}', (table) => {`)
      for (const fk of fksToDrop) {
        lines.push(`    table.dropForeign(['${fk.column}'])`)
      }
      lines.push(`  })`)
      lines.push('')
    }

    // Recreate FKs that were dropped in up() + restore old version of modified FKs
    const fksToRestore = [...fkDiff.droppedFks, ...fkDiff.modifiedFks.map(fk => ({
      ...fk,
      onDelete: fk.oldOnDelete,
      onUpdate: fk.oldOnUpdate,
    }))]
    if (fksToRestore.length > 0) {
      lines.push(`  await knex.schema.alterTable('${fkDiff.table}', (table) => {`)
      for (const fk of fksToRestore) {
        let ref = `    table.foreign('${fk.column}').references('${fk.referencedColumn}').inTable('${fk.referencedTable}')`
        if (fk.onDelete) ref += `.onDelete('${fk.onDelete}')`
        if (fk.onUpdate) ref += `.onUpdate('${fk.onUpdate}')`
        lines.push(ref)
      }
      lines.push(`  })`)
      lines.push('')
    }
  }

  if (changes.fkChanges.length > 0) {
    lines.push(`  } finally {`)
    lines.push(`    if (isSQLite) await knex.raw('PRAGMA foreign_keys = ON')`)
    lines.push(`  }`)
    lines.push('')
  }

  // 5. Drop DAG junction tables first (before entity tables due to FK)
  for (const { entity } of changes.newTables) {
    if (entity.type === 'dag') {
      const parentsTable = (entity as EntityDefinition & { parentsTable?: string }).parentsTable ?? `${(entity as any).table}_parents`
      lines.push(`  await knex.schema.dropTableIfExists('${parentsTable}')`)
    }
  }

  // 4. Drop new tables (reverse of create)
  for (const { table } of changes.newTables.slice().reverse()) {
    lines.push(`  await knex.schema.dropTableIfExists('${table}')`)
  }

  if (lines.length === 0) {
    lines.push('  // No changes')
  }

  return lines.join('\n')
}

/**
 * Log diff summary to console.
 */
function logDiffSummary(diff: SchemaDiff): void {
  if (diff.newTables.length > 0) {
    console.log(`  📦 ${diff.newTables.length} new tables`)
  }

  if (diff.droppedTables.length > 0) {
    console.log(`  🗑️  ${diff.droppedTables.length} dropped tables`)
  }

  if (diff.alteredTables.length > 0) {
    console.log(`  🔧 ${diff.alteredTables.length} altered tables`)
  }

  if (diff.indexChanges.length > 0) {
    console.log(`  🔑 ${diff.indexChanges.length} index changes`)
  }

  if (diff.fkChanges.length > 0) {
    const total = diff.fkChanges.reduce((sum, fk) => sum + fk.newFks.length + fk.droppedFks.length + fk.modifiedFks.length, 0)
    console.log(`  🔗 ${total} foreign key changes`)
  }

  const modifiedCount = diff.alteredTables.reduce((sum, t) => sum + t.modifiedColumns.length, 0)
  if (modifiedCount > 0) {
    console.log(`  ⚠️  ${modifiedCount} column type changes (DATA LOSS: drop + recreate)`)
  }
}

/**
 * Format schema drift into a human-readable message.
 * Used by server startup to display drift details before failing.
 */
export function formatDriftMessage(drift: SchemaDiff): string {
  const lines: string[] = []

  lines.push('Schema drift detected! Database schema does not match entity definitions.')
  lines.push('')

  if (drift.newTables.length > 0) {
    lines.push(`Missing tables (${drift.newTables.length}):`)
    for (const { table } of drift.newTables) {
      lines.push(`  - ${table}`)
    }
    lines.push('')
  }

  if (drift.alteredTables.length > 0) {
    lines.push(`Tables with missing columns (${drift.alteredTables.length}):`)
    for (const alteration of drift.alteredTables) {
      const newCols = alteration.newColumns.map(c => c.name).join(', ')
      if (newCols) {
        lines.push(`  - ${alteration.table}: missing columns [${newCols}]`)
      }
    }
    lines.push('')
  }

  if (drift.droppedTables.length > 0) {
    lines.push(`Extra tables in database (${drift.droppedTables.length}):`)
    for (const table of drift.droppedTables) {
      lines.push(`  - ${table}`)
    }
    lines.push('')
  }

  if (drift.indexChanges.length > 0) {
    lines.push(`Tables with index changes (${drift.indexChanges.length}):`)
    for (const idx of drift.indexChanges) {
      const parts: string[] = []
      if (idx.newIndexes.length > 0) parts.push(`${idx.newIndexes.length} missing`)
      if (idx.droppedIndexes.length > 0) parts.push(`${idx.droppedIndexes.length} extra`)
      lines.push(`  - ${idx.table}: ${parts.join(', ')} indexes`)
    }
    lines.push('')
  }

  if (drift.fkChanges.length > 0) {
    lines.push(`Tables with FK changes (${drift.fkChanges.length}):`)
    for (const fk of drift.fkChanges) {
      const parts: string[] = []
      if (fk.newFks.length > 0) parts.push(`${fk.newFks.length} missing`)
      if (fk.droppedFks.length > 0) parts.push(`${fk.droppedFks.length} extra`)
      if (fk.modifiedFks.length > 0) parts.push(`${fk.modifiedFks.length} modified`)
      lines.push(`  - ${fk.table}: ${parts.join(', ')} foreign keys`)
    }
    lines.push('')
  }

  lines.push('Run "pnpm migrate:dev" to generate and apply migrations.')

  return lines.join('\n')
}

/**
 * Generate Knex createTable code for a single entity.
 * Useful for testing the code generation logic.
 *
 * @remarks Exported for testing purposes.
 */
export function generateTableCode(entity: EntityDefinition): string[] {
  const lines: string[] = []
  const tableName = (entity as EntityDefinition & { table?: string }).table

  if (!tableName) {
    return lines
  }

  lines.push(`await knex.schema.createTable('${tableName}', (table) => {`)

  // Generate columns from fields
  const fieldNames = new Set(Object.keys(entity.fields ?? {}))
  if (entity.fields) {
    for (const [fieldName, field] of Object.entries(entity.fields) as [string, FieldDefinition][]) {
      if (!field.db || field.db.virtual) continue
      const columnDef = generateColumnDefinition(fieldName, field)
      lines.push(`  ${columnDef}`)
    }
  }

  // Add timestamps if entity has timestamps: true (skip if already declared in fields)
  const hasTimestamps = (entity as EntityDefinition & { timestamps?: boolean }).timestamps
  if (hasTimestamps) {
    if (!fieldNames.has('created_at') || !fieldNames.has('updated_at')) lines.push(`  // timestamps`)
    if (!fieldNames.has('created_at')) lines.push(`  table.timestamp('created_at').nullable().defaultTo(knex.fn.now())`)
    if (!fieldNames.has('updated_at')) lines.push(`  table.timestamp('updated_at').nullable().defaultTo(knex.fn.now())`)
  }

  // Add audit fields if entity has audit: true (skip if already declared in fields)
  const hasAudit = (entity as EntityDefinition & { audit?: boolean }).audit
  if (hasAudit) {
    if (!fieldNames.has('created_by') || !fieldNames.has('updated_by')) lines.push(`  // audit`)
    if (!fieldNames.has('created_by')) lines.push(`  table.string('created_by', 26).nullable()`)
    if (!fieldNames.has('updated_by')) lines.push(`  table.string('updated_by', 26).nullable()`)
  }

  lines.push(`})`)

  return lines
}
